import React, { useCallback, useEffect, useMemo, useState } from "react";
import SettingsOverscanIcon from "@mui/icons-material/SettingsOverscan";
import { IconButton } from "src/components/IconButton/IconButton";
import _ from "lodash";
import { checkOS } from "src/utils";

const DEFAULT_ZOOM_STEP = 0.2;

function getXY(e: any): any {
  let x = 0;
  let y = 0;
  if (e.touches && e.touches.length) {
    x = e.touches[0].pageX;
    y = e.touches[0].pageY;
  } else {
    x = e.pageX;
    y = e.pageY;
  }
  return { x, y };
}

function Cond(props: any): JSX.Element | null {
  if (!props.condition) return null;
  return <React.Fragment>{props.children}</React.Fragment>;
}

export interface ILightboxState {
  x: number;
  y: number;
  zoom: number;
  rotate: number;
  loading?: boolean;
  moving?: boolean;
  current?: number;
  multi?: boolean;
}

export interface ILightboxProps {
  image?: string;
  title?: string;
  images?: string[];
  startIndex?: number;
  zoomStep?: number;
  onClose?: (event: any) => any;
  allowZoom?: boolean;
  allowRotate?: boolean;
  allowReset?: boolean;
  buttonAlign?: string;
  showTitle?: boolean;
  keyboardInteraction?: boolean;
  // doubleClickZoom?: number; // disabled for now as it interferes with default values.
  clickOutsideToExit?: boolean;
  defaultValues?: ILightboxState;
  onChange?: (state: ILightboxState) => any;
  /** Called when the browser cannot decode `image`, so the caller can show its own message. */
  onError?: (event: React.SyntheticEvent<HTMLImageElement>) => void;
}

const stopSideEffect = (e: any): void => e.stopPropagation();
const minZoom = 0.1;
const maxZoom = 10;

const createTransform = (x: any, y: any, zoom: any, rotate: any): string =>
  `translate3d(${x}px,${y}px,0px) scale(${zoom}) rotate(${rotate}deg)`;

const getCurrentImage = (s: any, p: any): string => {
  if (!s.multi) return p.image ?? "";
  return p.images[s.current]?.url ?? p.images?.[s.current] ?? "";
};

const getCurrentTitle = (s: any, p: any): string => {
  if (!s.multi) return p.title ?? "";
  return p.images?.[s.current]?.title ?? "";
};

const settings = {
  x: 0,
  y: 0,
  zoom: 1,
  rotate: 0,
  loading: false,
  moving: false,
  multi: false,
};

export const Lightbox = (props: ILightboxProps): JSX.Element => {
  const {
    keyboardInteraction = true,
    allowZoom = true,
    allowRotate = true,
    buttonAlign = "flex-end",
    showTitle = true,
    allowReset = true,
    defaultValues,
    images,
    onChange,
    onClose,
    onError,
  } = props;

  const [initX, setInitX] = useState<number>(0);
  const [initY, setInitY] = useState<number>(0);
  const [lastX, setLastX] = useState<number>(defaultValues?.x ?? 0);
  const [lastY, setLastY] = useState<number>(defaultValues?.y ?? 0);

  // STATE
  const [x, setX] = useState<number>(defaultValues?.x ?? 0);
  const [y, setY] = useState<number>(defaultValues?.y ?? 0);
  const [zoom, setZoom] = useState<number>(defaultValues?.zoom ?? 1);
  const [rotate, setRotate] = useState<number>(defaultValues?.rotate ?? 0);
  const [loading, setLoading] = useState<boolean>(defaultValues?.loading ?? true);
  const [moving, setMoving] = useState<boolean>(defaultValues?.loading ?? false);
  const [multi, setMulti] = useState<boolean>(defaultValues?.loading ?? false);

  const [current, setCurrent] = useState<number>(props?.startIndex ?? 0);

  useEffect(() => {
    if (images && images?.length > 1 && !multi) {
      setMulti(true);
    } else if (images && images?.length === 1 && multi) {
      setMulti(false);
    }
  }, [images, multi]);

  const boxState = useMemo((): ILightboxState => {
    return {
      x,
      y,
      zoom,
      rotate,
      loading,
      moving,
      current,
      multi,
    };
  }, [x, y, zoom, rotate, loading, moving, current, multi]);
  const [updateParent, setUpdateParent] = useState<boolean>(false);

  // Ensure that this useEffect doesn't fire off too often.
  useEffect(() => {
    if (!!onChange && !!boxState && updateParent) {
      onChange(boxState);
      setUpdateParent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxState, updateParent]);

  const image = useMemo(() => getCurrentImage(boxState, props), [boxState, props]);
  const title = useMemo(() => getCurrentTitle(boxState, props), [boxState, props]);

  //const canvasRef: any = useRef();
  const resetZoom = useCallback(() => {
    setZoom(defaultValues?.zoom ?? 1);
    setUpdateParent(true);
  }, [defaultValues?.zoom]);

  const navigateImage = useCallback(
    (direction: any, e: any) => {
      stopSideEffect(e);
      let current = 0;
      switch (direction) {
        case "next":
          current = current + 1;
          break;
        case "prev":
          current = current - 1;
          break;
      }
      if (images && current >= images?.length) current = 0;
      else if (images && current < 0) current = images?.length - 1;
      setX(defaultValues?.x ?? 0);
      setY(defaultValues?.y ?? 0);
      setZoom(defaultValues?.zoom ?? 1);
      setRotate(defaultValues?.rotate ?? 0);
      setCurrent(current);
      setLoading(true);
      setUpdateParent(true);
    },
    [defaultValues?.rotate, defaultValues?.x, defaultValues?.y, defaultValues?.zoom, images?.length]
  );

  const startMove = useCallback(
    (e: any): void => {
      setMoving(true);
      const xy = getXY(e);
      setInitX(xy.x - lastX);
      setInitY(xy.y - lastY);
    },
    [lastX, lastY]
  );

  const duringMove = useCallback(
    (e: any): void => {
      if (!moving) return;
      const xy = getXY(e);
      setLastX(xy.x - initX);
      setLastY(xy.y - initY);
      setX(xy.x - initX);
      setY(xy.y - initY);
    },
    [initX, initY, moving]
  );

  const endMove = useCallback((e: any): void => {
    setMoving(false);
    setUpdateParent(true);
  }, []);

  const applyZoom = useCallback(
    (type: string) => {
      const { zoomStep = DEFAULT_ZOOM_STEP } = props;
      switch (type) {
        case "in":
          setZoom((prev) => prev + zoomStep);
          setUpdateParent(true);
          break;
        case "out":
          setZoom((prev) => prev - zoomStep);
          setUpdateParent(true);
          break;
        case "reset":
          resetZoom();
          break;
      }
    },
    [props, resetZoom]
  );

  const applyRotate = useCallback((type: string) => {
    switch (type) {
      case "cw":
        setRotate((prev) => prev + 90);
        setUpdateParent(true);
        break;
      case "acw":
        setRotate((prev) => prev - 90);
        setUpdateParent(true);
        break;
    }
  }, []);

  useEffect(() => {
    const cancelWheel = (event: any): void => event.preventDefault();
    document.body.addEventListener("wheel", cancelWheel, { passive: false });
    return () => {
      document.body.removeEventListener("wheel", cancelWheel);
    };
  }, []);

  const handleWheel = useCallback(
    (event: React.WheelEvent): void => {
      const axisToUse = event.deltaY;
      if (Math.floor(axisToUse) === 0) {
        return;
      }
      const zoomStep = props.zoomStep ?? 0.1;
      const delta = axisToUse > 0 ? -zoomStep : zoomStep;
      setZoom((prev) => {
        const newZoom = prev + delta;
        if (newZoom <= maxZoom && newZoom >= minZoom) {
          return prev + delta;
        } else {
          return prev;
        }
      });
      setUpdateParent(true);
    },
    [props.zoomStep]
  );

  const reset = useCallback((): void => {
    setX(settings?.x);
    setY(settings?.y);
    setZoom(settings?.zoom);
    setRotate(settings?.rotate);
    setUpdateParent(true);
  }, []);

  const centerImage = useCallback((e: any): void => {
    stopSideEffect(e);
    setLastX(0);
    setLastY(0);
    setX(0);
    setY(0);
  }, []);

  const exit = useCallback(
    (e: any): void => {
      if (typeof props.onClose === "function") return props.onClose(e);
      console.error("No Exit function passed on prop: onClose. Clicking the close button will do nothing");
    },
    [props]
  );

  const shouldShowReset = useMemo(() => {
    return (
      x !== defaultValues?.x ||
      y !== defaultValues?.y ||
      zoom !== defaultValues?.zoom ||
      rotate !== defaultValues?.rotate
    );
  }, [defaultValues?.rotate, defaultValues?.x, defaultValues?.y, defaultValues?.zoom, rotate, x, y, zoom]);

  const canvasClick = useCallback(
    (e: any): void => {
      const { clickOutsideToExit = true } = props;
      if (clickOutsideToExit && zoom <= 1) return exit(e);
    },
    [exit, props, zoom]
  );

  const keyboardNavigation = useCallback(
    (e: any) => {
      const { allowZoom = true, allowReset = true } = props;
      switch (e.key) {
        case "ArrowLeft":
          if (multi && zoom === 1) navigateImage("prev", e);
          else if (zoom > 1) setX((prev) => prev - 20);
          break;
        case "ArrowRight":
          if (multi && zoom === 1) navigateImage("next", e);
          else if (zoom > 1) setX((prev) => prev + 20);
          break;
        case "ArrowUp":
          if (zoom > 1) setY((prev) => prev + 20);
          break;
        case "ArrowDown":
          if (zoom > 1) setY((prev) => prev - 20);
          break;
        case "+":
          if (allowZoom) applyZoom("in");
          break;
        case "-":
          if (allowZoom) applyZoom("out");
          break;
        case "Escape":
          if (allowReset && shouldShowReset) reset();
          else exit(e);
          break;
      }
      setUpdateParent(true);
    },
    [applyZoom, exit, multi, navigateImage, props, reset, shouldShowReset, zoom]
  );

  useEffect(() => {
    document.body.classList.add("lb-open-lightbox");
    if (keyboardInteraction) document.addEventListener("keyup", keyboardNavigation);
    // Cleanup
    return () => {
      document.body.classList.remove("lb-open-lightbox");
      if (keyboardInteraction) document.removeEventListener("keyup", keyboardNavigation);
    };
  }, [keyboardInteraction, keyboardNavigation]);

  if (!image) {
    console.warn("Not showing lightbox because no image(s) was supplied");
    return <></>;
  }

  const _reset = allowReset && shouldShowReset;

  return (
    <div className="lb-container">
      <div className="lb-header" style={{ justifyContent: buttonAlign }}>
        <Cond condition={showTitle && title}>
          <div
            className="lb-title"
            style={{
              display: buttonAlign === "center" ? "none" : "flex",
              //@ts-ignore
              order: buttonAlign === "flex-start" ? "2" : "unset",
            }}
          >
            <span title={title} style={{ textAlign: buttonAlign === "flex-start" ? "right" : "left" }}>
              {title}
            </span>
          </div>
        </Cond>
        <div
          title="Reset To Default"
          //@ts-ignore
          style={{ order: buttonAlign === "flex-start" ? "1" : "unset" }}
          className={`lb-button lb-icon-reset lb-hide-mobile reload ${_reset ? "" : "lb-disabled"}`}
          onClick={() => reset()}
        ></div>
        <IconButton
          tooltipProps={{ title: "re-center image" }}
          onClick={centerImage}
          disabled={boxState.x === 0 && boxState.y === 0}
          size="large"
        >
          <SettingsOverscanIcon />
        </IconButton>
        <Cond condition={multi}>
          <div
            title="Previous"
            className="lb-button lb-icon-arrow prev lb-hide-mobile"
            onClick={(e) => navigateImage("prev", e)}
          ></div>
          <div
            title="Next"
            className="lb-button lb-icon-arrow next lb-hide-mobile"
            onClick={(e) => navigateImage("next", e)}
          ></div>
        </Cond>
        <Cond condition={allowZoom}>
          <div
            title="Zoom In"
            className={`lb-button lb-icon-zoomin zoomin ${zoom >= maxZoom ? "lb-disabled" : ""}`}
            onClick={() => applyZoom("in")}
          ></div>
          <div
            title="Zoom Out"
            className={`lb-button lb-icon-zoomout zoomout ${zoom <= minZoom ? "lb-disabled" : ""}`}
            onClick={() => applyZoom("out")}
          ></div>
        </Cond>
        <Cond condition={allowRotate}>
          <div
            title="Rotate left"
            className="lb-button lb-icon-rotate rotatel"
            onClick={() => applyRotate("acw")}
          ></div>
          <div
            title="Rotate right"
            className="lb-button lb-icon-rotate rotater"
            onClick={() => applyRotate("cw")}
          ></div>
        </Cond>
        {onClose && (
          <div
            title="Close"
            className="lb-button lb-icon-close close"
            style={{ order: buttonAlign === "flex-start" ? "-1" : "unset" }}
            onClick={(e) => exit(e)}
          ></div>
        )}
      </div>
      <div
        className={`lb-canvas${loading ? " lb-loading" : ""}`}
        //ref={canvasRef}
        onClick={(e) => canvasClick(e)}
        onWheel={(e) => handleWheel(e)}
      >
        <img
          draggable="false"
          style={{
            transform: createTransform(x, y, zoom, rotate),
            cursor: zoom > 1 ? "grab" : "unset",
            transition: moving ? "none" : "all 0.1s",
          }}
          onMouseDown={(e) => startMove(e)}
          onTouchStart={(e) => startMove(e)}
          onMouseMove={(e) => duringMove(e)}
          onTouchMove={(e) => duringMove(e)}
          onMouseUp={(e) => endMove(e)}
          onMouseLeave={(e) => endMove(e)}
          onTouchEnd={(e) => endMove(e)}
          onClick={(e) => stopSideEffect(e)}
          // onDoubleClick={(e) => shockZoom(e)}
          onLoad={(e) => setLoading(false)}
          onError={(e) => {
            // `loading` otherwise only clears on a successful load, so an undecodable file
            // leaves the canvas spinning on black with nothing to explain it.
            setLoading(false);
            onError?.(e);
          }}
          className={`lb-img${loading ? " lb-loading" : ""}`}
          title={title}
          src={image}
          alt={title}
        />
        <div className="mobile-controls lb-show-mobile">
          {multi ? (
            <div
              title="Previous"
              className="lb-button lb-icon-arrow prev"
              onClick={(e) => navigateImage("prev", e)}
            ></div>
          ) : null}
          {_reset ? <div title="Reset" className="lb-button lb-icon-reset reload" onClick={reset}></div> : null}
          {multi ? (
            <div title="Next" className="lb-button lb-icon-arrow next" onClick={(e) => navigateImage("next", e)}></div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
